"use client";

import { Mail, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faqaccordion() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "What materials do you offer for custom engraving?",
    answer: "We specialize in high-durability materials including industrial-grade phenolic, two-color acrylic, stainless steel, brass, and anodized aluminum. Each material is selected for its resistance to harsh environments and long-term legibility."
  }, {
    question: "Do you provide UL 508A compliant labels?",
    answer: "Yes, we are fully equipped to produce UL 508A compliant labels for panel builders. Our processes ensure that all markings meet the rigorous standards required for industrial control panels and electrical equipment."
  }, {
    question: "Can you handle large-scale submittal packages?",
    answer: "Absolutely. We provide comprehensive submittal packages for engineering projects, ensuring all documentation, material specifications, and proofs are organized and ready for your project approval process."
  }, {
    question: "What is the turnaround time for custom orders?",
    answer: "Turnaround times depend on the complexity and volume of your order. We pride ourselves on efficiency and will provide a clear timeline during the quoting process. For urgent requirements, please contact our team directly."
  }, {
    question: "Do you offer direct part marking (DPM)?",
    answer: "Yes, we provide precision direct part marking services. Using advanced laser technology, we can engrave permanent identification directly onto metal components, ensuring traceability and compliance for aerospace and manufacturing sectors."
  }, {
    question: "Are your arc flash labels customizable?",
    answer: "Our arc flash labels are fully customizable to meet your specific site requirements. We ensure all labels include the necessary hazard information, voltage levels, and safety warnings required by current electrical codes."
  }, {
    question: "How do I request a quote for my project?",
    answer: "You can request a quote by visiting our contact page or by emailing us your project specifications. Our team will review your requirements and provide a detailed estimate tailored to your specific needs."
  }, {
    question: "Do you work with electrical contractors?",
    answer: "We are a trusted partner for electrical contractors across the country. We understand the importance of precision and speed in the field, and we provide reliable labeling solutions that help you meet project deadlines."
  }, {
    question: "What industries do you serve?",
    answer: "We serve a wide range of industries, including power generation, water and wastewater treatment, manufacturing, marine, aerospace, and OEM equipment manufacturing. Our solutions are designed to withstand the most demanding industrial conditions."
  }, {
    question: "Can you engrave on customer-supplied parts?",
    answer: "Yes, we can perform laser engraving on customer-supplied metal parts. Please reach out to our team to discuss your specific material and part dimensions so we can ensure the best possible results for your application."
  }];
  return <section data-section-id="516" id="faqaccordion" className="bg-background text-foreground py-32">
      <div className="container mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-32">
              <div className="flex items-center gap-3 mb-8">
                <span className="h-px w-8 bg-primary" />
                <span className="font-default text-xs uppercase tracking-widest text-primary">
                  Expert Support
                </span>
              </div>

              <h2 className="font-default text-foreground text-4xl sm:text-5xl lg:text-[3.25rem] leading-[1.05] tracking-tight mb-6">
                Common
                <br />
                Inquiries.
              </h2>

              <p className="font-default text-muted-foreground text-base sm:text-lg leading-relaxed mb-10 max-w-md">
                Answers to the most frequent questions regarding our industrial engraving and labeling services.
              </p>

              <div className="border border-border bg-card rounded-lg p-6 max-w-md relative overflow-hidden shadow-md">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 shrink-0">
                    <Mail className="h-4 w-4 text-primary" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="font-default text-foreground text-lg mb-2 leading-tight">
                      Need more help?
                    </p>
                    <Link to="/ContactPage" className="font-default text-sm text-foreground inline-flex items-center gap-1.5 group/link border-b border-foreground/30 hover:border-primary hover:text-primary transition-colors duration-300 pb-0.5">
                      Contact our team
                      <span className="transition-transform duration-300 group-hover/link:translate-x-1">
                        &rarr;
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={i} value={`item-${i}`} className="border-b border-border last:border-b-0 data-[state=open]:bg-card/50 transition-colors duration-300 rounded-lg px-2 sm:px-4 -mx-2 sm:-mx-4">
                  <AccordionTrigger className="hover:no-underline py-6 sm:py-7 group/trigger [&>svg]:hidden">
                    <span className="font-default text-foreground text-lg sm:text-xl lg:text-[1.375rem] leading-snug pr-6 text-left flex-1">
                      {faq.question}
                    </span>
                    <span className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-all duration-500 group-data-[state=open]/trigger:rotate-[135deg] group-data-[state=open]/trigger:bg-primary group-data-[state=open]/trigger:text-primary-foreground group-data-[state=open]/trigger:border-primary">
                      <Plus className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-7 pr-12 sm:pr-16">
                    <p className="font-default text-muted-foreground text-base sm:text-[17px] leading-[1.7]">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}